// ---------------------------------------------------------------------------
// Zustand über die Zeit (PLAN.md §16c): Szenen halten fest, was sich in ihnen ändert (`StoryNode.changes`) – die Verfassung einer Figur
// (geboren, gesund, verletzt, tot), ihr Aufenthaltsort und bei wem ein Gegenstand ist. Der Zustand eines Elements zu einer Szene ergibt
// sich aus allen Änderungen davor in Weltzeit. Vorschläge kommen aus dem Text: „stirbt“, „wird verletzt“, „reist nach …“, „nimmt … an
// sich“ neben einem verknüpften Element (`findStateHints`); ✓ macht sie zur Änderung der Szene.
// ---------------------------------------------------------------------------

import type { StoryCtx } from './ctx.js';
import { entityTitle, narrativeOrder, worldOrder } from './narrative.js';
import { refIndex } from './refs.js';
import { isScene } from './scene.js';
import { priorLabel, priorOf } from './series.js';
import { spanAt } from './spans.js';
import { timeMarkAt } from './timeMarks.js';
import { CONDITIONS, STATE_KINDS, TRAIT_MAX, type Condition, type IgnoredTerm, type PriorSource, type StateChange, type StateKind, type StoryNode } from './types.js';

export const isStateKind = (k: string): k is StateKind => (STATE_KINDS as readonly string[]).includes(k);
export const isCondition = (v: string | null): v is Condition => v !== null && (CONDITIONS as readonly string[]).includes(v);

/** Die Änderungen einer Szene */
export const changesOf = (scene: Pick<StoryNode, 'changes'>): StateChange[] => scene.changes ?? [];

/** Wie ein Element im Zustand vorkommt: Figur, Ort, Gegenstand (kann bei jemandem sein) oder anderes */
export type StateRole = 'person' | 'place' | 'item' | 'other';

/** Vordefinierte Klassen, die weder Figur noch Gegenstand sind */
const NOT_ITEMS = new Set(['Organisation', 'Konzept']);

export function stateRoleOf(ctx: StoryCtx, entityId: string): StateRole {
  const e = ctx.entities.get(entityId);
  const t = e ? ctx.types.get(e.typeId) : undefined;
  if (!t) return 'other';
  if (t.category === 'place') return 'place';
  if (t.category !== 'object') return 'other';
  if (t.builtin && t.name === 'Person') return 'person';
  return t.builtin && NOT_ITEMS.has(t.name) ? 'other' : 'item';
}

/** Prüft eine Änderung, bevor sie an einer Szene gespeichert wird; liefert eine Meldung oder null. */
export function checkStateChange(ctx: StoryCtx, c: Pick<StateChange, 'entityId' | 'kind' | 'value' | 'from' | 'trait'>): string | null {
  if (!isStateKind(c.kind)) return `Unbekannte Art von Zustand: „${c.kind}“.`;
  if (!ctx.entities.has(c.entityId)) return 'Die Entität der Änderung gibt es nicht.';
  if (c.from !== undefined && c.kind !== 'besitz') return 'Von wem etwas kommt, gibt es nur beim Besitz.';
  if (c.trait !== undefined && c.kind !== 'merkmal') return 'Ein Merkmal gibt es nur bei der Art „merkmal“.';
  if (c.kind === 'merkmal') {
    if (!c.trait?.trim()) return 'Welches Merkmal? (z. B. „Haare“, „Narbe“)';
    if (c.trait.length > TRAIT_MAX || (c.value !== null && c.value.length > TRAIT_MAX)) return `Merkmal und Ausprägung haben höchstens ${TRAIT_MAX} Zeichen.`;
    return c.value === null || c.value.trim() !== '' ? null : 'Die Ausprägung fehlt (oder das Merkmal fällt weg).';
  }
  if (c.kind === 'verfassung') return isCondition(c.value) ? null : `Verfassung ist ${CONDITIONS.join(', ')}.`;
  if (c.kind === 'ort') return c.value && stateRoleOf(ctx, c.value) === 'place' ? null : 'Ein Aufenthaltsort ist ein Ort der Bibliothek.';
  if (c.value === c.entityId) return 'Ein Gegenstand kann nicht bei sich selbst sein.';
  if (c.from !== undefined && (c.from === c.entityId || !ctx.entities.has(c.from))) return 'Von wem der Gegenstand kommt, steht nicht in der Bibliothek.';
  return c.value === null || ctx.entities.has(c.value) ? null : 'Bei wem der Gegenstand ist, steht nicht in der Bibliothek.';
}

/** Ist es dieselbe Änderung (Element, Art, Wert; beim Merkmal auch welches)? */
export const sameChange = (a: Pick<StateChange, 'entityId' | 'kind' | 'value' | 'trait'>, b: Pick<StateChange, 'entityId' | 'kind' | 'value' | 'trait'>): boolean =>
  a.entityId === b.entityId && a.kind === b.kind && a.value === b.value && (a.kind !== 'merkmal' || sameTrait(a.trait ?? '', b.trait ?? ''));

/** Dasselbe Merkmal: ohne Unterschied in Groß- und Kleinschreibung bzw. Leerraum */
export const sameTrait = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

// ---------------------------------------------------------------------------
// Feld „Besitzer“ als Ausgangszustand (AP-31): Ein Gegenstand, dessen Feld „Besitzer“ eine Figur bzw. einen Ort nennt (verknüpft oder mit
// Namen), ist von Anfang an dort – bis zur ersten Änderung seines Besitzes in Weltzeit.
// ---------------------------------------------------------------------------

const OWNER_FIELDS = new Set(['besitzer', 'besitzerin', 'owner', 'eigentümer', 'eigentümerin']);
const OWNER_REF = /\[@([A-Za-z][A-Za-z0-9_]{0,29})\]|\]\(@([A-Za-z][A-Za-z0-9_]{0,29})\)|(?<![\w@])@([A-Za-z][A-Za-z0-9_]{0,29})/;

/** Bei wem ein Gegenstand laut seinem Feld „Besitzer“ zu Beginn ist (eine Figur bzw. ein Ort); null, wenn das Feld niemanden nennt */
export function initialHolder(ctx: StoryCtx, itemId: string): string | null {
  const e = ctx.entities.get(itemId);
  const t = e ? ctx.types.get(e.typeId) : undefined;
  if (!e || !t || stateRoleOf(ctx, itemId) !== 'item') return null;
  const field = t.fields.find((f) => OWNER_FIELDS.has(f.key.toLowerCase()) || OWNER_FIELDS.has(f.label.trim().toLowerCase()));
  const raw = field ? e.fields[field.key] : undefined;
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  const ok = (id: string | undefined): id is string => !!id && id !== itemId && (stateRoleOf(ctx, id) === 'person' || stateRoleOf(ctx, id) === 'place');
  const m = OWNER_REF.exec(raw);
  if (m) {
    const key = (m[1] ?? m[2] ?? m[3])!.toLowerCase();
    const id = ctx.story.entities.find((x) => x.refKey === key)?.id;
    return ok(id) ? id : null;
  }
  const name = raw.trim().toLowerCase();
  const hits = ctx.story.entities.filter((x) => ok(x.id) && entityTitle(ctx, x.id).trim().toLowerCase() === name);
  return hits.length === 1 ? hits[0]!.id : null;
}

/** Eine Änderung mit ihrer Szene */
export interface StateEvent {
  change: StateChange;
  sceneId: string;
  /** „Kapitel.Szene“, leer wenn nicht eingeordnet */
  label: string;
  title: string;
  /** Stelle der Szene in Weltzeit */
  world: number;
  /** betrifft das Element selbst oder bekommt es (als Besitzer) einen Gegenstand */
  role: 'self' | 'holder';
}

/** Alle Änderungen der Geschichte in Weltzeit (innerhalb einer Szene in ihrer Reihenfolge). */
export function stateEvents(ctx: StoryCtx): Omit<StateEvent, 'role'>[] {
  const labels = new Map(narrativeOrder(ctx).scenes.map((e) => [e.scene.id, e.label]));
  return worldOrder(ctx, ctx.story.nodes.filter(isScene)).flatMap((scene, world) =>
    changesOf(scene).map((change) => ({ change, sceneId: scene.id, label: labels.get(scene.id) ?? '', title: String(scene.fields.title ?? '').trim(), world })),
  );
}

/** Verlauf eines Elements: seine Änderungen und (als Besitzer) die Gegenstände, die zu ihm kommen – in Weltzeit. */
export function stateHistory(ctx: StoryCtx, entityId: string, all = stateEvents(ctx)): StateEvent[] {
  return all.flatMap((e): StateEvent[] =>
    e.change.entityId === entityId ? [{ ...e, role: 'self' }] : e.change.kind === 'besitz' && e.change.value === entityId ? [{ ...e, role: 'holder' }] : [],
  );
}

/** Ein Wert des Zustands seit einer Szene (leere Szene: von Anfang an, aus dem Feld „Besitzer“ bzw. aus einer früheren Episode) */
export interface Since<T> {
  value: T;
  sceneId: string;
  label: string;
  /** aus der Vorgeschichte der Serie (series.ts): Episode und Szene dort */
  prior?: PriorSource;
}

/** Zustand eines Elements zu einem Zeitpunkt */
export interface EntityState {
  condition: Since<Condition> | null;
  place: Since<string> | null;
  /** Gegenstand: bei wem er ist (null: bei niemandem, verloren) */
  holder: Since<string | null> | null;
  /** Figur bzw. Ort: welche Gegenstände bei ihr sind */
  holds: Since<string>[];
  /** Merkmale mit ihrer Ausprägung (AP-31), in der Reihenfolge, in der sie zuerst vorkommen */
  traits: (Since<string> & { trait: string })[];
}

/** Ausgangslage: wo ein Gegenstand zu Beginn ist (null: bei niemandem) und seit wann (leer: von Anfang an) */
type Start = { value: string | null } & Pick<Since<string>, 'sceneId' | 'label' | 'prior'>;

/** Stand aus einer früheren Episode als Wert „seit …“ */
const fromPrior = <T>(p: PriorSource & { value: T }): Since<T> => ({ value: p.value, sceneId: '', label: priorLabel(p.episode), prior: { episode: p.episode, scene: p.scene } });

/**
 * Gegenstände, die zu Beginn bei jemandem sind: aus der Vorgeschichte der Serie (am Ende der vorigen Episode), sonst laut Feld „Besitzer“
 * (Gegenstand → Figur bzw. Ort)
 */
function initialHolders(ctx: StoryCtx): Map<string, Start> {
  const out = new Map<string, Start>();
  for (const e of ctx.story.entities) {
    const prior = priorOf(ctx, e.id)?.holder;
    if (prior) {
      out.set(e.id, fromPrior(prior));
      continue;
    }
    const holder = initialHolder(ctx, e.id);
    if (holder) out.set(e.id, { value: holder, sceneId: '', label: '' });
  }
  return out;
}

/**
 * Zustand eines Elements nach den Änderungen `events` (in Weltzeit), ausgehend von seinem Stand am Ende der vorigen Episode (Serie) und
 * den Besitzern zu Beginn
 */
function stateFrom(ctx: StoryCtx, entityId: string, events: Omit<StateEvent, 'role'>[], initial: ReadonlyMap<string, Start>): EntityState {
  const state: EntityState = { condition: null, place: null, holder: null, holds: [], traits: [] };
  const items = new Map<string, Since<string>>();
  const prior = priorOf(ctx, entityId);
  if (prior?.condition) state.condition = fromPrior(prior.condition);
  if (prior?.place) state.place = fromPrior(prior.place);
  const first = initial.get(entityId);
  if (first) state.holder = first;
  for (const [item, start] of initial) if (start.value === entityId) items.set(item, { ...start, value: item });
  const traits = new Map<string, Since<string> & { trait: string }>();
  for (const t of prior?.traits ?? []) traits.set(t.trait.trim().toLowerCase(), { ...fromPrior(t), trait: t.trait });
  for (const e of events) {
    const c = e.change;
    const since = { sceneId: e.sceneId, label: e.label };
    if (c.entityId === entityId) {
      if (c.kind === 'verfassung' && isCondition(c.value)) state.condition = { ...since, value: c.value };
      else if (c.kind === 'ort' && c.value) state.place = { ...since, value: c.value };
      else if (c.kind === 'besitz') state.holder = { ...since, value: c.value };
      else if (c.kind === 'merkmal' && c.trait) {
        const key = c.trait.trim().toLowerCase();
        if (c.value === null) traits.delete(key);
        else traits.set(key, { ...since, value: c.value, trait: c.trait.trim() });
      }
    }
    if (c.kind === 'besitz') {
      if (c.value === entityId) items.set(c.entityId, { ...since, value: c.entityId });
      else items.delete(c.entityId);
    }
  }
  state.holds = [...items.values()];
  state.traits = [...traits.values()];
  return state;
}

const initialCache = new WeakMap<StoryCtx, Map<string, Start>>();
const initialOf = (ctx: StoryCtx): Map<string, Start> => {
  let m = initialCache.get(ctx);
  if (!m) initialCache.set(ctx, (m = initialHolders(ctx)));
  return m;
};

/** Zustand eines Elements am Ende der Geschichte (Weltzeit) */
export const stateOf = (ctx: StoryCtx, entityId: string, all = stateEvents(ctx)): EntityState => stateFrom(ctx, entityId, all, initialOf(ctx));

/** Zustand zur Szene: vor ihr (`before`) und was sich in ihr für das Element ändert (`here`) */
export interface StateAt {
  before: EntityState;
  here: StateChange[];
}

export function stateAt(ctx: StoryCtx, entityId: string, sceneId: string, all = stateEvents(ctx)): StateAt {
  const scene = ctx.nodes.get(sceneId);
  const at = all.find((e) => e.sceneId === sceneId)?.world ?? worldOrder(ctx, ctx.story.nodes.filter(isScene)).findIndex((s) => s.id === sceneId);
  return {
    before: stateFrom(
      ctx,
      entityId,
      all.filter((e) => e.world < at),
      initialOf(ctx),
    ),
    here: scene ? changesOf(scene).filter((c) => c.entityId === entityId || (c.kind === 'besitz' && c.value === entityId)) : [],
  };
}

// ---------------------------------------------------------------------------
// Vorschläge aus dem Text: Je Satz suchen Signalwörter („stirbt“, „verletzt“, „reist nach“, „nimmt“) das passende verknüpfte Element –
// die Figur davor (Subjekt) bzw. bei Taten an anderen die danach (Objekt), den Ort bzw. Gegenstand danach. Was in Rede oder Gedanken
// steht, zählt nicht. Ein Vorschlag ist nur ein Hinweis; ✓ übernimmt ihn, ✕ merkt ihn im Dokument als „keine Änderung“.
// ---------------------------------------------------------------------------

/** Ein verknüpftes Element im Text (Lage im Text, Rolle im Zustand) */
export interface HintLink {
  key: string;
  from: number;
  to: number;
  role: StateRole;
}

/** Eine mögliche Änderung im Text */
export interface StateHint {
  /** das Signalwort (bzw. die Wendung) */
  from: number;
  to: number;
  word: string;
  kind: StateKind;
  /** wessen Zustand (Verweisschlüssel) */
  key: string;
  /** Verfassung bzw. Schlüssel des Orts / Besitzers; null: verloren */
  value: string | null;
  /** Besitz: wer den Gegenstand weitergibt bzw. verliert (Schlüssel), soweit der Satz es sagt */
  giver?: string;
  /** der Satz als Klartext (Beleg) */
  quote: string;
}

const W = '(?<![\\p{L}\\p{N}_-])';
const E = '(?![\\p{L}\\p{N}_-])';
const words = (list: string): RegExp => new RegExp(`${W}(?:${list})${E}`, 'giu');

/** Hilfsverben und „sich“: davor steht das Geschehen am Subjekt („wird verletzt“, „verletzt sich“) */
const PASSIVE = new RegExp(`${W}(?:ist|war|wird|wurde|werden|wurden|sind|waren|worden|sich|lag|liegt|blieb|bleibt)${E}`, 'iu');
/** zwischen Signalwort und Figur nur Artikel und Füllwörter: umgestellter Satz, die Figur ist das Subjekt */
const INVERTED = /^\s+(?:(?:der|die|das|auch|nun|noch|schließlich|endlich|dann|da|kleine|kleinen|junge|junges|alte)\s+)*$/iu;
/** von wem bzw. wodurch – eine Figur danach ist nie das Subjekt */
const AGENT = /(?:von|vom|durch)\s+(?:(?:der|die|das|den|dem|des|einem|einer|eines)\s+)?$/iu;

interface Rule {
  kind: StateKind;
  re: RegExp;
  /** Verfassung */
  value?: Condition;
  /**
   * wer: `subject` – die Figur davor (sonst danach); `object` – die Figur danach; `either` – Hilfsverb/„sich“ davor: Subjekt, sonst Figur
   * danach, sonst Subjekt
   */
  who: 'subject' | 'object' | 'either';
  /** Besitz: `take` (das Subjekt bekommt ihn), `give` (eine Figur danach bekommt ihn), `lose` (verloren bzw. an einem Ort danach) */
  owner?: 'take' | 'give' | 'lose';
}

const RULES: Rule[] = [
  // Tod
  { kind: 'verfassung', value: 'tot', who: 'subject', re: words('stirbt|starb|starben|sterben|gestorben|verstirbt|verstarb|verstorben|ertrinkt|ertrank|ertrunken|verblutet|verblutete|ums Leben|tot|leblos|getötet|erschlagen|erschossen|erstochen|hingerichtet|gehängt|zu Tode') },
  { kind: 'verfassung', value: 'tot', who: 'either', re: words('ermordet|vergiftet|erwürgt|enthauptet|erhängt|erdrosselt|tötet|tötete|erschlägt|erschlug|erschießt|erschoss|ersticht|erstach|ermordete|vergiftete|erwürgte|erdrosselte|richtet hin|richtete hin') },
  // Verletzung
  { kind: 'verfassung', value: 'verletzt', who: 'either', re: words('verletzt|verletzte|verwundet|verwundete|schwer getroffen|angeschossen') },
  { kind: 'verfassung', value: 'verletzt', who: 'subject', re: words('blutet|blutete|bricht sich|brach sich|stürzt schwer|stürzte schwer') },
  // Genesung
  { kind: 'verfassung', value: 'gesund', who: 'either', re: words('genest|genas|genesen|erholt sich|erholte sich|geheilt|heilt|heilte|wieder gesund|gesundet|gesundete') },
  // Ortswechsel: Bewegung mit einem Ort danach
  {
    kind: 'ort',
    who: 'subject',
    re: words('reist|reiste|reisen|geht|ging|gehen|fährt|fuhr|fahren|flieht|floh|fliehen|flüchtet|flüchtete|zieht|zog|kehrt|kehrte|kommt|kam|kommen|gelangt|gelangte|erreicht|erreichte|betritt|betrat|eilt|eilte|läuft|lief|rennt|rannte|reitet|ritt|segelt|segelte|wandert|wanderte|begibt sich|begab sich|bricht auf|brach auf|landet|landete|wird gebracht|wurde gebracht|gebracht'),
  },
  // Besitz
  { kind: 'besitz', who: 'subject', owner: 'take', re: words('nimmt|nahm|ergreift|ergriff|packt|packte|hebt|hob|steckt|steckte|erhält|erhielt|bekommt|bekam|findet|fand|stiehlt|stahl|erbt|erbte|kauft|kaufte|greift nach|griff nach|holt|holte|entreißt|entriss|raubt|raubte') },
  { kind: 'besitz', who: 'subject', owner: 'give', re: words('gibt|gab|übergibt|übergab|überreicht|überreichte|reicht|reichte|schenkt|schenkte|verkauft|verkaufte|vererbt|vererbte|bringt|brachte|leiht|lieh') },
  { kind: 'besitz', who: 'subject', owner: 'lose', re: words('verliert|verlor|verloren|lässt fallen|ließ fallen|wirft weg|warf weg|versteckt|versteckte|vergräbt|vergrub|gestohlen') },
];

/** Satzgrenzen (Satzzeichen mit Leerraum danach bzw. Zeilenende) */
function sentences(text: string): [number, number][] {
  const out: [number, number][] = [];
  let start = 0;
  for (const m of text.matchAll(/[.!?;:]+(?=\s|$)|\n/g)) {
    const end = m.index + m[0].length;
    if (end > start) out.push([start, end]);
    start = end;
  }
  if (start < text.length) out.push([start, text.length]);
  return out;
}

const inside = (ranges: readonly [number, number][], at: number): boolean => ranges.some(([a, b]) => a <= at && at < b);

/**
 * Mögliche Änderungen in einem Text (ein Absatz o. Ä.): `links` sind die verknüpften Elemente darin (Lage im Text, Rolle), `quoted` die
 * Bereiche in Rede und Gedanken. Je Satz und Änderung (Element, Art, Wert) höchstens ein Vorschlag – das erste Signalwort.
 */
export function findStateHints(text: string, links: readonly HintLink[], quoted: readonly [number, number][] = []): StateHint[] {
  const out: StateHint[] = [];
  for (const [sFrom, sTo] of sentences(text)) {
    const own = links.filter((l) => l.from >= sFrom && l.to <= sTo && !inside(quoted, l.from));
    if (own.length === 0) continue;
    const seen = new Set<string>();
    const sentence = text.slice(sFrom, sTo);
    const quote = sentence.replace(/\s+/g, ' ').trim();
    const persons = own.filter((l) => l.role === 'person');
    for (const rule of RULES) {
      rule.re.lastIndex = 0;
      for (const m of sentence.matchAll(rule.re)) {
        const from = sFrom + m.index;
        const to = from + m[0].length;
        if (inside(quoted, from) || own.some((l) => l.from <= from && from < l.to)) continue;
        const before = persons.filter((l) => l.to <= from && !AGENT.test(text.slice(sFrom, l.from)));
        const after = persons.filter((l) => l.from >= to);
        // Inversion („dann starb Josephe“): die Figur direkt hinter dem Signalwort ist das Subjekt
        const inverted = after[0] && INVERTED.test(text.slice(to, after[0].from)) ? after[0] : undefined;
        const subject = inverted ?? before[before.length - 1] ?? after[0];
        const passive = PASSIVE.test(text.slice(before[before.length - 1]?.to ?? sFrom, from).replace(/\s+$/, '')) || /^\s+sich\b/u.test(text.slice(to, to + 6));
        let hint: Omit<StateHint, 'from' | 'to' | 'word' | 'quote'> | null = null;
        if (rule.kind === 'verfassung') {
          const target = rule.who === 'subject' ? subject : rule.who === 'object' ? after[0] : passive ? subject : (after[0] ?? subject);
          if (target) hint = { kind: 'verfassung', key: target.key, value: rule.value! };
        } else if (rule.kind === 'ort') {
          // der erste Ort danach, ohne „aus“/„von“ davor (wer aus Rom kommt, ist danach nicht in Rom)
          const place = own.find((l) => l.role === 'place' && l.from >= to && !/(?:aus|von|vom)\s+(?:(?:der|die|das|den|dem|des)\s+)?$/iu.test(text.slice(to, l.from)));
          const who = before[before.length - 1] ?? after.find((l) => !place || l.to <= place.from);
          if (place && who && place.from - to < 80) hint = { kind: 'ort', key: who.key, value: place.key };
        } else {
          const item = own.find((l) => l.role === 'item' && l.from >= to) ?? (rule.owner === 'lose' ? own.filter((l) => l.role === 'item' && l.to <= from).pop() : undefined);
          if (item) {
            if (rule.owner === 'take') {
              const holder = before[before.length - 1] ?? after.find((l) => l.to <= item.from);
              if (holder) hint = { kind: 'besitz', key: item.key, value: holder.key };
            } else if (rule.owner === 'give') {
              const giver = before[before.length - 1];
              const to2 = after.find((l) => l.key !== giver?.key);
              if (to2) hint = { kind: 'besitz', key: item.key, value: to2.key, ...(giver ? { giver: giver.key } : {}) };
            } else {
              const place = own.find((l) => l.role === 'place' && l.from >= to);
              const loser = passive ? undefined : before[before.length - 1];
              hint = { kind: 'besitz', key: item.key, value: place?.key ?? null, ...(loser ? { giver: loser.key } : {}) };
            }
          }
        }
        if (!hint) continue;
        const k = `${hint.kind}\n${hint.key}\n${hint.value}`;
        if (seen.has(k)) continue;
        seen.add(k);
        out.push({ ...hint, from, to, word: m[0], quote });
      }
    }
  }
  return out.sort((a, b) => a.from - b.from);
}

/** Ein Absatz Markdown als sichtbarer Text samt verknüpfter Elemente und der Bereiche in Rede und Gedanken */
function linkedText(md: string, roleOf: (key: string) => StateRole | null): { text: string; links: HintLink[]; quoted: [number, number][] } {
  let text = '';
  const links: HintLink[] = [];
  const quoted: [number, number][] = [];
  const add = (key: string, from: number): void => {
    const role = roleOf(key.toLowerCase());
    if (role) links.push({ key: key.toLowerCase(), from, to: text.length, role });
  };
  const walk = (s: string): void => {
    for (let i = 0; i < s.length; ) {
      if (s[i] === '[' && s[i - 1] !== '\\') {
        const span = spanAt(s, i);
        const mark = span ? null : timeMarkAt(s, i);
        if (span || mark) {
          const r = (span ?? mark)!;
          const start = text.length;
          walk(s.slice(r.textFrom, r.textTo));
          if (span?.role === 'rede' || span?.role === 'gedanke') quoted.push([start, text.length]);
          i = r.to;
          continue;
        }
        const passage = /^\[([^[\]\n]+)\]\(@([A-Za-z]\w{0,29})\)/.exec(s.slice(i));
        if (passage) {
          const start = text.length;
          text += passage[1]!.replace(/\*\*|~~|[*`]/g, '');
          add(passage[2]!, start);
          i += passage[0].length;
          continue;
        }
        const word = /^\[@([A-Za-z]\w{0,29})\]/.exec(s.slice(i));
        if (word) {
          const start = text.length - (/[\p{L}\p{N}_-]+$/u.exec(text)?.[0].length ?? 0);
          if (start < text.length) add(word[1]!, start);
          i += word[0].length;
          continue;
        }
      }
      text += s[i];
      i++;
    }
  };
  walk(md);
  return { text, links, quoted };
}

/** Mögliche Änderungen in einem Text (Markdown, z. B. dem Draft einer Szene), je Absatz; Lage im sichtbaren Text des Absatzes. */
export function stateHintsIn(ctx: StoryCtx, md: string): StateHint[] {
  const refs = refIndex(ctx);
  const roleOf = (key: string): StateRole | null => {
    const e = refs.get(key);
    return e ? stateRoleOf(ctx, e.id) : null;
  };
  return md.split(/\n\s*\n/).flatMap((block) => {
    const { text, links, quoted } = linkedText(block, roleOf);
    return links.length > 0 ? findStateHints(text, links, quoted) : [];
  });
}

// ---------------------------------------------------------------------------
// „Keine Änderung“ merken: als Ausnahme des Dokuments (`TextDocument.ignored`) in der Form `{term: '@wer', before: '!art', after: wert}`
// (Wert: Verfassung bzw. „@schlüssel“, leer = verloren). Gilt im Dokument, solange es das Element nennt.
// ---------------------------------------------------------------------------

export const stateIgnored = (h: Pick<StateHint, 'kind' | 'key' | 'value'>): IgnoredTerm => ({
  term: `@${h.key}`,
  before: `!${h.kind}`,
  after: h.value === null ? '' : h.kind === 'verfassung' ? h.value : `@${h.value}`,
});

export const isStateIgnored = (x: IgnoredTerm): boolean => x.term.startsWith('@') && x.before.startsWith('!');

export const isStateIgnoredIn = (ignored: readonly IgnoredTerm[] | undefined, h: Pick<StateHint, 'kind' | 'key' | 'value'>): boolean => {
  const x = stateIgnored(h);
  return !!ignored?.some((y) => y.term === x.term && y.before === x.before && y.after === x.after);
};
